import type {
  DealSow,
  SalesDealQuestionnaireSummary,
  SalesDealSummary,
  SalesDealTemplateFieldSummary,
  SavedDealEstimate,
} from '@protoss/shared';
import { getEffectiveEstimateData, isAdhocSectionKey } from '@protoss/shared';
import { SkeletonBlock } from '@protoss/ui';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { getDealStatusPresentation } from '../features/sales/components/DealViewSurface';
import { DealSowDocView } from '../features/sales/components/DealSowDocView';
import { salesApi } from '../features/sales/services/salesApi';
import { computeHoursRange } from '../features/sales/utils/estimateHours';
import {
  getAnswerKey,
  resolveFieldDisplayValue,
  type FieldDisplayValue,
} from '../features/sales/utils/field-display-value';
import {
  buildQuestionnaireFieldLookup,
  isFieldVisibleByRules,
  type QuestionnaireAnswerMap,
} from '../features/sales/utils/questionnaire-field-visibility';
import { usePageTitle } from '../hooks/usePageTitle';
import './deal-page.css';
import './desktop-workspace-page.css';

/**
 * Desktop workspace (COS-TBD desktop shell).
 *
 * The coSow desktop app (coSow Live) hosts these two routes in its Workspace window:
 *   /so/desktop                 - the workspace's deals, search, "New deal from a call"
 *   /so/desktop/deals/:dealUuid - one deal as four read-only steps: Start, Answers,
 *                                 Estimate, SOW (the "Guided deal" design)
 *
 * Built once here; Electron only hosts it. Data comes from the same salesApi calls and
 * helpers the deal pages use (listDeals, getDeal, getDealQuestionnaire, getSavedEstimate,
 * getSow, getEffectiveEstimateData, resolveFieldDisplayValue, DealSowDocView). Nothing is
 * edited here: editing stays on /so/deal/:uuid.
 *
 * The dark look is scoped to `.desktop-ws` (tokens overridden in desktop-workspace-page.css),
 * so the rest of the app keeps its theme. The same token values are mirrored in the desktop
 * card's CSS so both surfaces read as one product.
 *
 * `window.cosowDesktop` is the bridge the desktop app's preload exposes. Without it (a normal
 * browser) the desktop-only controls are hidden.
 */

export interface CosowDesktopBridge {
  /** Open the live call card, optionally with a customer / deal preloaded. */
  startCall: (args: { customer?: string | null; dealUuid?: string | null }) => unknown;
  /** Open the desktop app's gear menu (Setup, Settings, Reload, About). */
  openMenu?: () => unknown;
  version?: string;
}

declare global {
  interface Window {
    cosowDesktop?: CosowDesktopBridge;
  }
}

function useDesktopBridge(): CosowDesktopBridge | null {
  return typeof window !== 'undefined' && window.cosowDesktop ? window.cosowDesktop : null;
}

const WELCOME_KEY = 'cosow.desktop.welcomeSeen';

function readWelcomeSeen(): boolean {
  try {
    return window.localStorage.getItem(WELCOME_KEY) === '1';
  } catch {
    return true;
  }
}

function writeWelcomeSeen(): void {
  try {
    window.localStorage.setItem(WELCOME_KEY, '1');
  } catch {
    // Per-browser convenience only.
  }
}

const numberFormat = new Intl.NumberFormat('en-US');
const currencyFormat = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function dealName(deal: SalesDealSummary): string {
  return deal.title?.trim() || deal.company?.name || 'Untitled deal';
}

const CATEGORY_LABELS: Record<string, string> = { bpr: 'BPR' };

function categoryLabel(key: string | null | undefined): string | null {
  if (!key) return null;
  const known = CATEGORY_LABELS[key];
  if (known) return known;
  const words = key.replace(/_/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function dealSubtitle(deal: SalesDealSummary): string {
  return (
    [deal.vendorProduct?.productName, categoryLabel(deal.dealCategoryKey)]
      .filter(Boolean)
      .join(', ') || 'No product yet'
  );
}

// ── Shared chrome ────────────────────────────────────────────────────────────

function GearIcon() {
  return (
    <svg className="desktop-ws__icon" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
    </svg>
  );
}

function ChatIcon() {
  return (
    <svg className="desktop-ws__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v11H9l-5 4z" />
    </svg>
  );
}

function CallIcon() {
  return (
    <svg className="desktop-ws__icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3z" />
      <path d="M19 11a7 7 0 0 1-14 0M12 18v3" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      className="desktop-ws__icon desktop-ws__icon--check"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M5 12.5l4.5 4.5L19 7" />
    </svg>
  );
}

function DesktopTopbar({
  crumb,
  children,
}: {
  crumb: React.ReactNode;
  children?: React.ReactNode;
}) {
  const navigate = useNavigate();
  const bridge = useDesktopBridge();
  return (
    <header className="desktop-ws__topbar">
      <button
        type="button"
        className="desktop-ws__brand"
        onClick={() => void navigate('/so/desktop')}
        aria-label="coSow deals"
      >
        <i className="desktop-ws__brand-mark" aria-hidden="true" />
        coSow
      </button>
      <span className="desktop-ws__crumb">{crumb}</span>
      <span className="desktop-ws__grow" />
      {children}
      {bridge?.openMenu ? (
        <button
          type="button"
          className="desktop-ws__btn desktop-ws__btn--ghost desktop-ws__btn--icon"
          aria-label="Settings menu"
          onClick={() => void bridge.openMenu?.()}
        >
          <GearIcon />
        </button>
      ) : null}
    </header>
  );
}

function WelcomeModal({ onClose }: { onClose: () => void }) {
  const startRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    startRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <>
      <div className="desktop-ws__scrim" aria-hidden="true" onClick={onClose} />
      <div
        className="desktop-ws__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="desktop-ws-welcome-title"
      >
        <div className="desktop-ws__kicker desktop-ws__kicker--accent">Welcome</div>
        <h2 id="desktop-ws-welcome-title" className="desktop-ws__modal-title">
          coSow scopes a deal for you
        </h2>
        <p className="desktop-ws__modal-text">
          Bring a discovery call. coSow turns it into an hours estimate and an SOW you can send.
          Here is how it works:
        </p>
        <ol className="desktop-ws__welcome-steps">
          <li>
            <span>1</span>Start a call, or open a deal
          </li>
          <li>
            <span>2</span>coSow fills the answers while you talk
          </li>
          <li>
            <span>3</span>Get the estimate and the SOW
          </li>
        </ol>
        <div className="desktop-ws__modal-actions">
          <button type="button" className="desktop-ws__linkbtn" onClick={onClose}>
            Skip
          </button>
          <button
            ref={startRef}
            type="button"
            className="desktop-ws__btn desktop-ws__btn--primary desktop-ws__btn--lg"
            onClick={onClose}
          >
            Start
          </button>
        </div>
      </div>
    </>
  );
}

function useWelcome(): [boolean, () => void] {
  const [open, setOpen] = React.useState(() => !readWelcomeSeen());
  const close = React.useCallback(() => {
    writeWelcomeSeen();
    setOpen(false);
  }, []);
  return [open, close];
}

// ── Home: deals list ─────────────────────────────────────────────────────────

export function DesktopHomePage() {
  usePageTitle('Deals');
  const navigate = useNavigate();
  const bridge = useDesktopBridge();
  const { workspace } = useWorkspace();
  const [deals, setDeals] = React.useState<SalesDealSummary[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [query, setQuery] = React.useState('');
  const [welcomeOpen, closeWelcome] = useWelcome();

  const load = React.useCallback(async () => {
    setError(null);
    try {
      const { deals: rows } = await salesApi.listDeals();
      setDeals(rows);
    } catch {
      setError('Could not load the deals.');
      setDeals([]);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load, workspace?.uuid]);

  const filtered = React.useMemo(() => {
    const list = [...(deals ?? [])].sort((a, b) =>
      String(b.updatedAt ?? b.createdAt ?? '').localeCompare(
        String(a.updatedAt ?? a.createdAt ?? '')
      )
    );
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter((d) =>
      [d.title, d.company?.name, d.vendorProduct?.productName, categoryLabel(d.dealCategoryKey)]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [deals, query]);

  const newDealFromCall = () => {
    if (bridge) void bridge.startCall({ customer: null, dealUuid: null });
    else void navigate('/so/deal/new');
  };

  return (
    <div className="desktop-ws">
      <DesktopTopbar
        crumb={
          <>
            {workspace?.name ? <span>{workspace.name}</span> : null}
            {workspace?.name ? <span className="desktop-ws__muted"> / </span> : null}
            <b>Deals</b>
          </>
        }
      />
      <main className="desktop-ws__home">
        <div className="desktop-ws__home-head">
          <div>
            <div className="desktop-ws__kicker">Workspace</div>
            <h1 className="desktop-ws__title">Deals</h1>
            <p className="desktop-ws__lede">
              Start from a discovery call, or pick up a deal where you left it.
            </p>
          </div>
          <button
            type="button"
            className="desktop-ws__btn desktop-ws__btn--primary desktop-ws__btn--lg"
            onClick={newDealFromCall}
          >
            <CallIcon />
            New deal from a call
          </button>
        </div>

        <label className="desktop-ws__search">
          <span className="desktop-ws__sr-only">Search deals</span>
          <svg className="desktop-ws__icon" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="11" cy="11" r="7" />
            <path d="m20 20-3.5-3.5" />
          </svg>
          <input
            className="desktop-ws__search-input"
            type="search"
            placeholder="Search by deal, company or product"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {deals ? (
            <span className="desktop-ws__muted desktop-ws__num">
              {filtered.length} of {deals.length}
            </span>
          ) : null}
        </label>

        {error ? (
          <div className="desktop-ws__notice desktop-ws__notice--error" role="alert">
            <div>
              <div className="desktop-ws__notice-title">{error}</div>
              <div className="desktop-ws__notice-actions">
                <button type="button" className="desktop-ws__btn" onClick={() => void load()}>
                  Try again
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {deals === null ? (
          <div className="desktop-ws__deal-list" aria-busy="true">
            {[0, 1, 2, 3].map((i) => (
              <SkeletonBlock key={i} className="desktop-ws__deal-skeleton" />
            ))}
          </div>
        ) : filtered.length === 0 && !error ? (
          <div className="desktop-ws__empty">
            <b>{deals.length === 0 ? 'No deals yet' : 'No deal matches that search'}</b>
            {deals.length === 0
              ? 'Start a call and coSow drafts the deal from what is said.'
              : 'Try a company or product name.'}
          </div>
        ) : (
          <ul className="desktop-ws__deal-list">
            {filtered.map((deal) => {
              const status = getDealStatusPresentation(deal.status);
              const updated = formatDate(deal.updatedAt ?? deal.createdAt);
              return (
                <li key={deal.uuid}>
                  <button
                    type="button"
                    className="desktop-ws__deal-row"
                    onClick={() => void navigate(`/so/desktop/deals/${deal.uuid}`)}
                  >
                    <span className="desktop-ws__logo" aria-hidden="true">
                      {(deal.company?.name || dealName(deal)).slice(0, 2).toUpperCase()}
                    </span>
                    <span className="desktop-ws__deal-main">
                      <span className="desktop-ws__deal-name">{dealName(deal)}</span>
                      <span className="desktop-ws__deal-sub">
                        {deal.company?.name && deal.title ? `${deal.company.name}, ` : ''}
                        {dealSubtitle(deal)}
                      </span>
                    </span>
                    <span className={`desktop-ws__tag desktop-ws__tag--${status.variant}`}>
                      {status.label}
                    </span>
                    {updated ? (
                      <span className="desktop-ws__deal-date desktop-ws__num">{updated}</span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </main>
      {welcomeOpen ? <WelcomeModal onClose={closeWelcome} /> : null}
    </div>
  );
}

// ── Deal: four read-only steps ───────────────────────────────────────────────

const STEP_NAMES = ['Start', 'Answers', 'Estimate', 'SOW'] as const;
type StepIndex = 0 | 1 | 2 | 3;

interface AnswerRow {
  field: SalesDealTemplateFieldSummary;
  display: FieldDisplayValue;
}

interface AnswerSection {
  key: string;
  label: string;
  rows: AnswerRow[];
}

/** Visible questionnaire fields with their read-only display values, as DealViewSurface resolves them. */
function buildAnswerSections(q: SalesDealQuestionnaireSummary | null): AnswerSection[] {
  if (!q) return [];
  const answers: QuestionnaireAnswerMap = {};
  for (const section of q.sections) {
    for (const group of section.groups) {
      for (const field of group.fields) answers[getAnswerKey(field)] = field.value ?? null;
    }
  }
  for (const field of q.headerFields ?? []) answers[getAnswerKey(field)] = field.value ?? null;
  const lookup = buildQuestionnaireFieldLookup(q);
  const rules = q.conditionalRules ?? [];
  return [...q.sections]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .filter((section) => q.aiMode || !isAdhocSectionKey(section.sectionKey))
    .map((section) => ({
      key: section.sectionKey,
      label: section.label,
      rows: section.groups
        .flatMap((g) => g.fields)
        .filter((field) => isFieldVisibleByRules(field.fieldKey, rules, answers, lookup))
        .map((field) => ({ field, display: resolveFieldDisplayValue(field, answers) })),
    }))
    .filter((section) => section.rows.length > 0);
}

function displayText(display: FieldDisplayValue): string | null {
  switch (display.kind) {
    case 'text':
      return display.text;
    case 'pills':
      return display.pills.join(', ');
    case 'table':
      return `${display.rows.length} row${display.rows.length === 1 ? '' : 's'}`;
    default:
      return null;
  }
}

function StepHead({ index, title, lede }: { index: number; title: string; lede?: string }) {
  return (
    <div className="desktop-ws__step-head">
      <div className="desktop-ws__kicker">Step {index + 1} of 4</div>
      <h1 className="desktop-ws__title">{title}</h1>
      {lede ? <p className="desktop-ws__lede">{lede}</p> : null}
    </div>
  );
}

export function DesktopDealPage() {
  const { dealUuid = '' } = useParams<{ dealUuid: string }>();
  const navigate = useNavigate();
  const bridge = useDesktopBridge();
  const [welcomeOpen, closeWelcome] = useWelcome();

  const [deal, setDeal] = React.useState<SalesDealSummary | null>(null);
  const [dealError, setDealError] = React.useState<string | null>(null);
  const [questionnaire, setQuestionnaire] = React.useState<SalesDealQuestionnaireSummary | null>(
    null
  );
  const [savedEstimate, setSavedEstimate] = React.useState<SavedDealEstimate | null>(null);
  const [sow, setSow] = React.useState<DealSow | null>(null);
  const [loadingAssets, setLoadingAssets] = React.useState(true);
  const [step, setStep] = React.useState<StepIndex | null>(null);

  usePageTitle(deal ? dealName(deal) : 'Deal');

  React.useEffect(() => {
    if (!dealUuid) return;
    let cancelled = false;
    setDeal(null);
    setDealError(null);
    setLoadingAssets(true);
    setStep(null);
    void salesApi
      .getDeal(dealUuid)
      .then(({ deal: d }) => !cancelled && setDeal(d))
      .catch(() => !cancelled && setDealError('Could not load this deal.'));
    // Same calls the deal page makes (DealWorkspace reloadDealAssets + questionnaire load).
    void Promise.allSettled([
      salesApi.getDealQuestionnaire(dealUuid),
      salesApi.getSavedEstimate(dealUuid),
      salesApi.getSow(dealUuid),
    ]).then(([q, est, s]) => {
      if (cancelled) return;
      setQuestionnaire(q.status === 'fulfilled' ? q.value.questionnaire : null);
      setSavedEstimate(est.status === 'fulfilled' ? est.value.savedEstimate : null);
      setSow(s.status === 'fulfilled' ? s.value.sow : null);
      setLoadingAssets(false);
    });
    return () => {
      cancelled = true;
    };
  }, [dealUuid]);

  const sections = React.useMemo(() => buildAnswerSections(questionnaire), [questionnaire]);
  const answerStats = React.useMemo(() => {
    let total = 0;
    let answered = 0;
    let requiredOpen = 0;
    for (const s of sections) {
      for (const r of s.rows) {
        total += 1;
        const has = r.display.kind !== 'empty';
        if (has) answered += 1;
        else if (r.field.isRequired) requiredOpen += 1;
      }
    }
    return { total, answered, requiredOpen };
  }, [sections]);

  const effective = React.useMemo(
    () =>
      savedEstimate?.estimateData ? getEffectiveEstimateData(savedEstimate.estimateData) : null,
    [savedEstimate]
  );

  const done: [boolean, boolean, boolean, boolean] = [
    Boolean(deal),
    answerStats.total > 0 && answerStats.requiredOpen === 0,
    Boolean(effective),
    Boolean(sow),
  ];

  // Open on the furthest step that has something to show.
  React.useEffect(() => {
    if (step !== null || !deal || loadingAssets) return;
    setStep(sow ? 3 : effective ? 2 : answerStats.total > 0 ? 1 : 0);
  }, [step, deal, loadingAssets, sow, effective, answerStats.total]);

  const current: StepIndex = step ?? 0;
  const customer = deal?.company?.name || (deal ? dealName(deal) : null);

  const startCall = () => {
    if (!bridge || !deal) return;
    void bridge.startCall({ customer, dealUuid: deal.uuid });
  };

  const openFullDeal = () => void navigate(`/so/deal/${dealUuid}`);

  const renderStart = () => {
    if (!deal) return null;
    const facts: Array<[string, string | null]> = [
      ['Company', deal.company?.name ?? null],
      ['Product', deal.vendorProduct?.productName ?? null],
      ['Project type', categoryLabel(deal.dealCategoryKey)],
      ['Status', getDealStatusPresentation(deal.status).label],
      ['Created', formatDate(deal.createdAt)],
      ['Created by', deal.createdByDisplayName],
      ['Expected close', formatDate(deal.expectedCloseDate)],
      ['Source', deal.source === 'ai' ? 'Drafted by coSow from a call' : 'Entered by hand'],
    ];
    return (
      <>
        <StepHead index={0} title="Start" lede="Who the deal is for, and where it came from." />
        <div className="desktop-ws__card">
          <dl className="desktop-ws__facts">
            {facts.map(([label, value]) => (
              <div key={label} className="desktop-ws__fact">
                <dt>{label}</dt>
                <dd className={value ? undefined : 'desktop-ws__muted'}>{value ?? 'Not set'}</dd>
              </div>
            ))}
          </dl>
        </div>
        <div className="desktop-ws__actions">
          <button
            type="button"
            className="desktop-ws__btn desktop-ws__btn--primary desktop-ws__btn--lg"
            onClick={() => setStep(1)}
          >
            See the answers
          </button>
          {bridge ? (
            <button
              type="button"
              className="desktop-ws__btn desktop-ws__btn--lg"
              onClick={startCall}
            >
              <CallIcon />
              Start a call
            </button>
          ) : null}
        </div>
      </>
    );
  };

  const renderAnswers = () => {
    if (!questionnaire || sections.length === 0) {
      return (
        <>
          <StepHead index={1} title="Answers" />
          <div className="desktop-ws__empty">
            <b>No answers yet</b>
            {deal?.scopeSourced
              ? 'This deal was scoped from an uploaded list, so it has no questionnaire.'
              : 'Start a call and coSow fills the answers from what is said.'}
          </div>
        </>
      );
    }
    const pct = answerStats.total ? answerStats.answered / answerStats.total : 0;
    return (
      <>
        <StepHead
          index={1}
          title="Answers"
          lede="What coSow and your team captured. Change answers on the full deal page."
        />
        <div className="desktop-ws__count">
          <span>
            <b className="desktop-ws__num">
              {answerStats.answered} of {answerStats.total}
            </b>{' '}
            answered
            {answerStats.requiredOpen > 0 ? (
              <span className="desktop-ws__muted">
                {' '}
                ({answerStats.requiredOpen} required still open)
              </span>
            ) : null}
          </span>
          <progress
            className="desktop-ws__meter"
            max={1}
            value={pct}
            aria-label="Answered questions"
          />
        </div>
        {sections.map((section, i) => (
          <details key={section.key} className="desktop-ws__exp" open={i === 0}>
            <summary>
              <svg
                className="desktop-ws__icon desktop-ws__chev"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
              {section.label}
              <span className="desktop-ws__grow" />
              <span className="desktop-ws__muted desktop-ws__num">
                {section.rows.filter((r) => r.display.kind !== 'empty').length} of{' '}
                {section.rows.length}
              </span>
            </summary>
            <ul className="desktop-ws__checklist">
              {section.rows.map(({ field, display }) => {
                const text = displayText(display);
                const state = text ? 'ok' : field.isRequired ? 'open' : 'skip';
                return (
                  <li
                    key={field.fieldKey}
                    className={`desktop-ws__check desktop-ws__check--${state}`}
                  >
                    <span className="desktop-ws__check-mark" aria-hidden="true" />
                    <span className="desktop-ws__check-label">{field.label}</span>
                    <span className="desktop-ws__check-answer">
                      {text ?? (field.isRequired ? 'Needs you' : 'Not answered')}
                    </span>
                  </li>
                );
              })}
            </ul>
          </details>
        ))}
        <div className="desktop-ws__actions">
          <button
            type="button"
            className="desktop-ws__btn desktop-ws__btn--primary desktop-ws__btn--lg"
            onClick={() => setStep(2)}
          >
            See the estimate
          </button>
          <button
            type="button"
            className="desktop-ws__btn desktop-ws__btn--lg"
            onClick={openFullDeal}
          >
            Edit answers
          </button>
        </div>
      </>
    );
  };

  const renderEstimate = () => {
    if (!effective || !savedEstimate) {
      return (
        <>
          <StepHead index={2} title="Estimate" />
          <div className="desktop-ws__empty">
            <b>No estimate yet</b>
            Generate it on the full deal page once the answers are in.
            <div className="desktop-ws__empty-actions">
              <button
                type="button"
                className="desktop-ws__btn desktop-ws__btn--primary"
                onClick={openFullDeal}
              >
                Open the full deal
              </button>
            </div>
          </div>
        </>
      );
    }
    const total = effective.summary.grandTotalHours;
    const range = computeHoursRange(total, savedEstimate.estimateData.estimateRange ?? null);
    const phases = effective.sections.filter((s) => s.hours > 0);
    const max = Math.max(1, ...phases.map((p) => p.hours));
    const notes = [
      ...(savedEstimate.rationale ? [savedEstimate.rationale] : []),
      ...savedEstimate.estimateData.audit.notes,
    ];
    return (
      <>
        <StepHead
          index={2}
          title="Estimate"
          lede={`Version ${savedEstimate.version ?? '1.0'}, generated ${formatDate(savedEstimate.generatedAt) ?? ''}.`}
        />
        <div className="desktop-ws__card desktop-ws__hero">
          <div>
            <div className="desktop-ws__kicker">Total effort</div>
            <div className="desktop-ws__big desktop-ws__num">
              {numberFormat.format(Math.round(total))}
              <small>hours</small>
            </div>
            {range ? (
              <div className="desktop-ws__range desktop-ws__num">
                Likely range {numberFormat.format(range.low)} to {numberFormat.format(range.high)} h
              </div>
            ) : null}
          </div>
          {savedEstimate.totalCost != null ? (
            <div className="desktop-ws__price">
              <b className="desktop-ws__num">{currencyFormat.format(savedEstimate.totalCost)}</b>
              <span>estimated fee</span>
            </div>
          ) : null}
        </div>
        <div className="desktop-ws__sub-head">By section</div>
        <ul className="desktop-ws__phases">
          {phases.map((p) => (
            <li key={p.sectionKey} className="desktop-ws__phase">
              <span className="desktop-ws__phase-name">{p.label}</span>
              <progress
                className="desktop-ws__meter"
                max={max}
                value={p.hours}
                aria-label={p.label}
              />
              <span className="desktop-ws__phase-hours desktop-ws__num">
                {numberFormat.format(Math.round(p.hours))} h
              </span>
            </li>
          ))}
        </ul>
        {notes.length > 0 ? (
          <details className="desktop-ws__exp desktop-ws__exp--spaced">
            <summary>
              <svg
                className="desktop-ws__icon desktop-ws__chev"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
              Why this number
            </summary>
            <ul className="desktop-ws__exp-body">
              {notes.map((n, i) => (
                <li key={i}>{n}</li>
              ))}
            </ul>
          </details>
        ) : null}
        <div className="desktop-ws__actions">
          <button
            type="button"
            className="desktop-ws__btn desktop-ws__btn--primary desktop-ws__btn--lg"
            onClick={() => setStep(3)}
          >
            {sow ? 'Open SOW' : 'See the SOW'}
          </button>
          <button
            type="button"
            className="desktop-ws__btn desktop-ws__btn--ghost desktop-ws__btn--lg"
            onClick={openFullDeal}
          >
            Ask about this estimate
          </button>
        </div>
      </>
    );
  };

  const renderSow = () => {
    if (!sow) {
      return (
        <>
          <StepHead index={3} title="SOW" />
          <div className="desktop-ws__empty">
            <b>No SOW yet</b>
            Generate it on the full deal page from the estimate.
            <div className="desktop-ws__empty-actions">
              <button
                type="button"
                className="desktop-ws__btn desktop-ws__btn--primary"
                onClick={openFullDeal}
              >
                Open the full deal
              </button>
            </div>
          </div>
        </>
      );
    }
    return (
      <>
        <StepHead
          index={3}
          title="SOW"
          lede="The draft built from the estimate. Edit the wording or export it on the full deal page."
        />
        <div className="desktop-ws__actions desktop-ws__actions--top">
          <button
            type="button"
            className="desktop-ws__btn desktop-ws__btn--primary desktop-ws__btn--lg"
            onClick={openFullDeal}
          >
            Edit or export
          </button>
          {effective ? (
            <span className="desktop-ws__muted desktop-ws__num">
              {numberFormat.format(Math.round(effective.summary.grandTotalHours))} h, version{' '}
              {sow.version ?? '1.0'}
            </span>
          ) : null}
        </div>
        <article className="desktop-ws__paper">
          <DealSowDocView dealUuid={dealUuid} />
        </article>
      </>
    );
  };

  return (
    <div className="desktop-ws">
      <DesktopTopbar
        crumb={
          <>
            <span>Deals</span>
            <span className="desktop-ws__muted"> / </span>
            <b>{deal ? dealName(deal) : 'Deal'}</b>
          </>
        }
      >
        {bridge && deal ? (
          <button type="button" className="desktop-ws__btn" onClick={startCall}>
            <CallIcon />
            <span className="desktop-ws__hide-sm">Start a call</span>
          </button>
        ) : null}
        <button
          type="button"
          className="desktop-ws__btn"
          aria-label="Ask coSow"
          onClick={openFullDeal}
          disabled={!deal}
        >
          <ChatIcon />
          <span className="desktop-ws__hide-sm">Ask coSow</span>
        </button>
      </DesktopTopbar>
      <div className="desktop-ws__deal">
        <aside className="desktop-ws__rail" aria-label="Deal steps">
          <div className="desktop-ws__rail-deal">
            <b>{deal ? dealName(deal) : 'Loading'}</b>
            <span>{deal ? dealSubtitle(deal) : ''}</span>
          </div>
          <h2 className="desktop-ws__rail-title">Steps</h2>
          <ol className="desktop-ws__steps">
            {STEP_NAMES.map((name, i) => {
              const isCur = i === current && step !== null;
              const mods = [
                done[i] ? 'desktop-ws__step--done' : '',
                isCur ? 'desktop-ws__step--current' : '',
              ]
                .filter(Boolean)
                .join(' ');
              return (
                <li key={name}>
                  <button
                    type="button"
                    className={`desktop-ws__step ${mods}`}
                    aria-current={isCur ? 'step' : undefined}
                    onClick={() => setStep(i as StepIndex)}
                    disabled={!deal}
                  >
                    <span className="desktop-ws__step-icon">
                      {done[i] && !isCur ? <CheckIcon /> : i + 1}
                    </span>
                    {name}
                    <span className="desktop-ws__step-state">
                      {isCur ? 'Now' : done[i] ? 'Done' : ''}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </aside>
        <main className="desktop-ws__main">
          <div className="desktop-ws__screen">
            {dealError ? (
              <div className="desktop-ws__notice desktop-ws__notice--error" role="alert">
                <div>
                  <div className="desktop-ws__notice-title">{dealError}</div>
                  <p>It may have been deleted, or it belongs to another workspace.</p>
                  <div className="desktop-ws__notice-actions">
                    <button
                      type="button"
                      className="desktop-ws__btn"
                      onClick={() => void navigate('/so/desktop')}
                    >
                      Back to deals
                    </button>
                  </div>
                </div>
              </div>
            ) : !deal || step === null ? (
              <div aria-busy="true" className="desktop-ws__loading">
                <SkeletonBlock className="desktop-ws__skeleton-title" />
                <SkeletonBlock className="desktop-ws__skeleton-card" />
              </div>
            ) : current === 0 ? (
              renderStart()
            ) : current === 1 ? (
              renderAnswers()
            ) : current === 2 ? (
              renderEstimate()
            ) : (
              renderSow()
            )}
          </div>
        </main>
      </div>
      {welcomeOpen ? <WelcomeModal onClose={closeWelcome} /> : null}
    </div>
  );
}
